import { useState } from 'react';
import {
  ApartmentOutlined,
  CheckOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  PaperClipOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';

import { MenuThaoTac } from '../../../../components/shared';
import { Badge, DropdownMenu } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { LOP_NHAN_TRANG_THAI, NHAN_TRANG_THAI, TONG_TRANG_THAI } from '../../constants';
import { phanTram, SO_AVATAR, TRANG_THAI_NHIEM_VU, trangThaiHien } from '../../nghiep-vu/nhiem-vu';
import type { DonViDanhBa, NutNhiemVu, TrangThaiNhiemVu } from '../../types-nhiem-vu';
import { DeadlineChip } from './DeadlineChip';
import { IconAi } from './IconAi';
import { QuickAddParticipant } from './QuickAddParticipant';
import { UserAvatar } from './UserAvatar';

export interface TaskRowActions {
  onOpen: (task: NutNhiemVu) => void;
  onAddChild: (parent: NutNhiemVu) => void;
  onDecompose: (parent: NutNhiemVu) => void;
  onEdit: (task: NutNhiemVu) => void;
  onDelete: (task: NutNhiemVu) => void;
  onStatus: (task: NutNhiemVu, status: TrangThaiNhiemVu) => void;
}

/** Quyền của người xem (ADR-UI-0005: không đủ quyền thì ẩn). */
export interface QuyenNhiemVu {
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

interface Props extends TaskRowActions {
  tasks: NutNhiemVu[];
  orgs: DonViDanhBa[];
  quyen: QuyenNhiemVu;
}

const THUT_REM = 1.5;
const LUOI = 'xl:grid-cols-[minmax(0,1fr)_8.5rem_13rem_9.5rem_2.5rem]';

interface RowCtx {
  actions: TaskRowActions;
  orgs: DonViDanhBa[];
  quyen: QuyenNhiemVu;
}

/**
 * Cây nhiệm vụ: nhóm "Cần làm" và "Đã hoàn thành", mỗi việc gốc kèm toàn bộ cây con có đường nối.
 * Không dùng `DataTable`: lưới cây nhiều cấp, mỗi dòng mở được và dưới `xl` gom cột thành hàng (như bản cũ).
 */
export function TaskTreeList({ tasks, orgs, quyen, ...actions }: Props) {
  const { t } = useT();
  const ctx: RowCtx = { actions, orgs, quyen };
  const canLam = tasks.filter((x) => x.status !== 'HOAN_THANH');
  const daXong = tasks.filter((x) => x.status === 'HOAN_THANH');
  return (
    <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
      <div
        aria-hidden
        className={cn(
          'hidden gap-4 border-b border-default bg-subtle px-4 py-2 text-2xs font-semibold uppercase tracking-wide text-content-muted xl:grid',
          LUOI,
        )}
      >
        <span>{t('vanBanNhiemVu.ds.cotNhiemVu')}</span>
        <span>{t('vanBanNhiemVu.ds.cotHan')}</span>
        <span>{t('vanBanNhiemVu.ds.cotDonVi')}</span>
        <span>{t('vanBanNhiemVu.ds.cotTrangThai')}</span>
        <span />
      </div>
      {canLam.length > 0 && <TaskGroup label={t('vanBanNhiemVu.ds.canLam')} tasks={canLam} ctx={ctx} />}
      {daXong.length > 0 && (
        <TaskGroup label={t('vanBanNhiemVu.ds.daXong')} tasks={daXong} ctx={ctx} defaultOpen={canLam.length === 0} />
      )}
    </div>
  );
}

function TaskGroup({
  label,
  tasks,
  ctx,
  defaultOpen = true,
}: {
  label: string;
  tasks: NutNhiemVu[];
  ctx: RowCtx;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 border-b border-default px-4 py-2.5 text-left text-sm font-semibold text-content-primary hover:bg-subtle"
      >
        {open ? <DownOutlined aria-hidden className="text-xs" /> : <RightOutlined aria-hidden className="text-xs" />}
        {label}
        <span className="rounded bg-subtle px-1.5 text-xs tabular-nums text-content-secondary">{tasks.length}</span>
      </button>
      {open && (
        <ul>
          {tasks.map((task) => (
            <TaskRow key={task.id} task={task} depth={0} guides={[]} isLast ctx={ctx} />
          ))}
        </ul>
      )}
    </section>
  );
}

interface RowProps {
  task: NutNhiemVu;
  depth: number;
  /** Với mỗi cấp tổ tiên (trừ cấp cha trực tiếp): đường dọc có chạy tiếp qua dòng này không. */
  guides: boolean[];
  isLast: boolean;
  ctx: RowCtx;
}

function TaskRow({ task, depth, guides, isLast, ctx }: RowProps) {
  const { t } = useT();
  // Việc đã xong thì thu gọn sẵn — việc con của nó không còn cần theo dõi.
  const [expanded, setExpanded] = useState(task.status !== 'HOAN_THANH');
  const hasChildren = task.children.length > 0;
  const isRoot = depth === 0;

  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        aria-label={t('vanBanNhiemVu.ds.xemChiTiet', { ten: task.title })}
        onClick={() => ctx.actions.onOpen(task)}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            ctx.actions.onOpen(task);
          }
        }}
        className={cn(
          'relative grid cursor-pointer grid-cols-1 gap-2 border-b border-default px-4 py-3 outline-none transition-colors hover:bg-subtle focus-visible:bg-subtle focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 xl:items-start xl:gap-4',
          LUOI,
          !isRoot && 'bg-page',
        )}
      >
        <TreeGuides depth={depth} guides={guides} isLast={isLast} hasOpenChildren={hasChildren && expanded} />
        <div className="flex min-w-0 gap-2" style={{ paddingLeft: `${depth * THUT_REM}rem` }}>
          {hasChildren ? (
            <button
              type="button"
              aria-expanded={expanded}
              aria-label={expanded ? t('vanBanNhiemVu.ds.thuCon') : t('vanBanNhiemVu.ds.moCon')}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center self-start rounded text-content-muted hover:bg-surface"
            >
              {expanded ? <DownOutlined className="text-2xs" /> : <RightOutlined className="text-2xs" />}
            </button>
          ) : (
            <span aria-hidden className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center self-start">
              {task.status === 'HOAN_THANH' ? (
                <CheckOutlined className="text-xs text-success-600" />
              ) : (
                <span className={cn('h-2 w-2 rounded-full', TONG_TRANG_THAI[trangThaiHien(task)].dot)} />
              )}
            </span>
          )}
          <TaskText task={task} isRoot={isRoot} />
        </div>

        {/* Dưới xl: các ô phụ gom thành một hàng dưới tiêu đề; từ xl `contents` trả chúng về lưới. */}
        <div
          className="flex flex-wrap items-start gap-x-6 gap-y-2 xl:contents"
          style={{ paddingLeft: `calc(${depth * THUT_REM}rem + 1.75rem)` }}
        >
          <DeadlineChip task={task} />
          <OrgCell task={task} ctx={ctx} />
          <StatusCell task={task} ctx={ctx} />
          <div className="ml-auto xl:ml-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <RowMenu task={task} ctx={ctx} />
          </div>
        </div>
      </div>

      {hasChildren && expanded && (
        <ul>
          {task.children.map((child, i) => (
            <TaskRow
              key={child.id}
              task={child}
              depth={depth + 1}
              guides={depth === 0 ? [] : [...guides, !isLast]}
              isLast={i === task.children.length - 1}
              ctx={ctx}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/** Lề trái dòng (px-4) + nửa ô biểu tượng 1.25rem: tâm cột đường nối của cấp 0. */
const NOI_GOC_REM = 1 + 0.625;
/** Tâm chấm/biểu tượng theo chiều dọc: py-3 + mt-0.5 + nửa ô 1.25rem. */
const NOI_KHUYU_REM = 0.75 + 0.125 + 0.625;
const NOI_DAY_ICON_REM = NOI_KHUYU_REM + 0.625;
/** Nhánh ngang dừng trước chấm của dòng con một khoảng nhỏ. */
const NOI_HO_REM = 0.875;
const DUONG_NOI = 'pointer-events-none absolute bg-dark-300';

function TreeGuides({
  depth,
  guides,
  isLast,
  hasOpenChildren,
}: {
  depth: number;
  guides: boolean[];
  isLast: boolean;
  hasOpenChildren: boolean;
}) {
  const x = (cap: number) => `${NOI_GOC_REM + cap * THUT_REM}rem`;
  return (
    // Bản thân khung cũng phải absolute: con thường của grid sẽ chiếm mất một ô cột.
    <span aria-hidden className="pointer-events-none absolute inset-0">
      {guides.map((tiep, cap) =>
        tiep ? <span key={cap} className={cn(DUONG_NOI, '-bottom-px top-0 w-px')} style={{ left: x(cap) }} /> : null,
      )}
      {depth > 0 && (
        <>
          <span
            className={cn(DUONG_NOI, 'top-0 w-px', !isLast && '-bottom-px')}
            style={{ left: x(depth - 1), height: isLast ? `${NOI_KHUYU_REM}rem` : undefined }}
          />
          <span
            className={cn(DUONG_NOI, 'h-px')}
            style={{ left: x(depth - 1), top: `${NOI_KHUYU_REM}rem`, width: `${THUT_REM - NOI_HO_REM + 0.625}rem` }}
          />
        </>
      )}
      {hasOpenChildren && (
        <span className={cn(DUONG_NOI, '-bottom-px w-px')} style={{ left: x(depth), top: `${NOI_DAY_ICON_REM}rem` }} />
      )}
    </span>
  );
}

function TaskText({ task, isRoot }: { task: NutNhiemVu; isRoot: boolean }) {
  const xong = task.status === 'HOAN_THANH';
  return (
    <div className="min-w-0">
      <p
        className={cn(
          'line-clamp-2 text-sm leading-snug text-content-primary',
          isRoot && 'font-semibold',
          xong && !isRoot && 'text-content-muted line-through',
        )}
      >
        {task.title}
      </p>
      {(task.description || task.attachment_filename) && (
        <p className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-content-muted">
          {task.attachment_filename && <PaperClipOutlined aria-hidden className="shrink-0" />}
          {task.description && <span className="truncate">{task.description}</span>}
        </p>
      )}
    </div>
  );
}

function OrgCell({ task, ctx }: { task: NutNhiemVu; ctx: RowCtx }) {
  const { t } = useT();
  const hien = task.participants.slice(0, SO_AVATAR);
  const an = task.participants.length - hien.length;
  return (
    <div className="min-w-0 space-y-1.5">
      <span
        className={cn(
          'inline-flex max-w-full items-center gap-1 rounded px-2 py-0.5 text-xs',
          task.lead_org_name ? 'bg-primary-50 text-primary-700' : 'bg-subtle text-content-muted',
        )}
      >
        <ApartmentOutlined aria-hidden />
        <span className="truncate">{task.lead_org_name ?? t('vanBanNhiemVu.ds.chuaCoDonVi')}</span>
      </span>
      <div className="flex items-center">
        <div className="flex -space-x-1">
          {hien.map((p) => (
            <UserAvatar key={p.user_id} name={p.user_name} />
          ))}
          {an > 0 && (
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border-2 border-[var(--bg-surface)] bg-subtle text-2xs font-semibold text-content-secondary">
              {t('vanBanNhiemVu.ds.themNguoi', { so: an })}
            </span>
          )}
        </div>
        {ctx.quyen.canUpdate && <QuickAddParticipant task={task} orgs={ctx.orgs} />}
      </div>
    </div>
  );
}

function StatusCell({ task, ctx }: { task: NutNhiemVu; ctx: RowCtx }) {
  const { t } = useT();
  const hien = trangThaiHien(task);
  const conDo = task.child_total - task.child_done;
  const items: MenuProps['items'] = TRANG_THAI_NHIEM_VU.map((s) => {
    const biChan = s === 'HOAN_THANH' && conDo > 0;
    return {
      key: s,
      label: biChan ? t('vanBanNhiemVu.ds.xongBiChan', { so: conDo }) : t(NHAN_TRANG_THAI[s]),
      disabled: s === task.status || biChan,
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        ctx.actions.onStatus(task, s);
      },
    };
  });
  return (
    <div className="space-y-1.5" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      {ctx.quyen.canUpdate ? (
        <DropdownMenu menu={{ items }}>
          <button
            type="button"
            aria-label={t('vanBanNhiemVu.ds.doiTrangThai', { trangThai: t(NHAN_TRANG_THAI[hien]) })}
            className={cn(
              'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500',
              LOP_NHAN_TRANG_THAI[hien],
            )}
          >
            {t(NHAN_TRANG_THAI[hien])}
            <DownOutlined aria-hidden className="text-2xs" />
          </button>
        </DropdownMenu>
      ) : (
        <Badge variant={TONG_TRANG_THAI[hien].badge} className="whitespace-nowrap">
          {t(NHAN_TRANG_THAI[hien])}
        </Badge>
      )}
      {task.child_total === 0 && task.progress > 0 && task.status !== 'HOAN_THANH' && (
        <ThanhNho
          nhan={t('vanBanNhiemVu.ds.tienDoPhanTram', { phanTram: task.progress })}
          phanTram={task.progress}
          lop="bg-primary-500"
        />
      )}
      {task.child_total > 0 && (
        <ThanhNho
          nhan={t('vanBanNhiemVu.ds.tienDoCon', { xong: task.child_done, tong: task.child_total })}
          phanTram={phanTram(task.child_done, task.child_total)}
          lop="bg-success-500"
        />
      )}
    </div>
  );
}

function ThanhNho({ nhan, phanTram: pt, lop }: { nhan: string; phanTram: number; lop: string }) {
  return (
    <div className="w-24">
      <p className="text-2xs tabular-nums text-content-muted">{nhan}</p>
      <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-subtle">
        <div className={cn('h-full rounded-full', lop)} style={{ width: `${pt}%` }} />
      </div>
    </div>
  );
}

function RowMenu({ task, ctx }: { task: NutNhiemVu; ctx: RowCtx }) {
  const { t } = useT();
  const { actions, quyen } = ctx;
  return (
    <div className="flex justify-end">
      <MenuThaoTac
        tenBanGhi={task.title}
        muc={[
          { khoa: 'xem', nhom: 'xem', icon: <EyeOutlined />, nhan: t('vanBanNhiemVu.chiTiet.tieuDe'), onChon: () => actions.onOpen(task) },
          { khoa: 'ai', icon: <IconAi />, nhan: t('vanBanNhiemVu.ds.aiPhanRa'), onChon: () => actions.onDecompose(task), hien: quyen.canCreate },
          { khoa: 'con', icon: <PlusOutlined />, nhan: t('vanBanNhiemVu.ds.giaoCon'), onChon: () => actions.onAddChild(task), hien: quyen.canCreate },
          { khoa: 'sua', icon: <EditOutlined />, nhan: t('vanBanNhiemVu.ds.sua'), onChon: () => actions.onEdit(task), hien: quyen.canUpdate },
          {
            khoa: 'xoa',
            nhom: 'pha-huy',
            icon: <DeleteOutlined />,
            nhan: t('vanBanNhiemVu.ds.xoa'),
            onChon: () => actions.onDelete(task),
            hien: quyen.canDelete,
          },
        ]}
      />
    </div>
  );
}
